// Reine Logik der Bedienleiste (Agent 5B, Runde 5), ohne React und ohne Importe, mit node --test prüfbar:
// Sortierung (Klick auf Spaltenkopf, Auswahl im Popover, Vergleich beim Sortieren geladener Treffer, Sortierung einer Smart View
// aus dem Close-Format lesen), Spaltenwahl (Aliase, feste Spalten, Standard), Textsuche in der Liste.

export interface SortEintrag {
  key: string;
  ab: boolean;
}

export const MAX_SORTFELDER = 3;

// ---------------------------------------------------------------------------
// Schlüssel
// ---------------------------------------------------------------------------

/** Namen, die dieselbe Spalte meinen, auch wenn der Katalog noch nicht geladen ist. */
export const ALIASE_STANDARD: Record<string, string> = { status_label: "status_id", name: "display_name" };

/** Anderer Name derselben Spalte (`status_label` -> `status_id`, `name` -> `display_name`). */
export function kanonisch(key: string, aliase: Record<string, string>): string {
  return aliase[key] ?? key;
}

/** Spaltenschlüssel einer Liste für die Anzeige zusammenräumen: Aliase auflösen, Doppelte und Unbekannte entfernen,
 *  feste Spalten (Name) an den Anfang. `gueltig` leer = alle erlaubt. */
export function spaltenBereinigen(keys: string[], aliase: Record<string, string>, gueltig: ReadonlySet<string> | null, fest: string[]): string[] {
  const ergebnis: string[] = [];
  for (const roh of keys) {
    const k = kanonisch(roh, aliase);
    if (gueltig && !gueltig.has(k)) continue;
    if (!ergebnis.includes(k)) ergebnis.push(k);
  }
  const vorn = fest.filter((k) => !gueltig || gueltig.has(k));
  return [...vorn, ...ergebnis.filter((k) => !vorn.includes(k))];
}

/** Spalten aus dem Katalog mit längerem Titel („Letzte Kommunikation“): ihr Spaltenkopf wird nicht auf Feldbreite gekürzt. */
export function kopfLang(key: string): boolean {
  return !/^(custom\.|display_name$|status_id$|status_label$|date_created$|date_updated$|kontaktwege$|primary_)/.test(key);
}

export function gleicheListe(a: readonly string[] | null | undefined, b: readonly string[] | null | undefined): boolean {
  if (!a || !b) return a === b;
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

// ---------------------------------------------------------------------------
// Sortierung
// ---------------------------------------------------------------------------

/** Klick auf einen Spaltenkopf: erst in der Startrichtung (Zeiten und Zahlen meist absteigend: `zuerstAb`), dann umgekehrt, dann aus.
 *  Weitere Sortierfelder fallen weg. */
export function sortKlick(aktuell: readonly SortEintrag[], key: string, zuerstAb = false): SortEintrag[] {
  const erstes = aktuell[0];
  if (!erstes || erstes.key !== key) return [{ key, ab: zuerstAb }];
  return erstes.ab === zuerstAb ? [{ key, ab: !zuerstAb }] : [];
}

/** Feld in der Sortierwahl angeklickt: nicht dabei -> wird das erste Sortierfeld (die bisherigen rücken nach, höchstens `max`);
 *  schon dabei -> die Richtung kippt, die Reihenfolge bleibt. */
export function sortWaehlen(aktuell: readonly SortEintrag[], key: string, ab: boolean, max = MAX_SORTFELDER): SortEintrag[] {
  const vorhanden = aktuell.findIndex((s) => s.key === key);
  if (vorhanden >= 0) return aktuell.map((s, i) => (i === vorhanden ? { key: s.key, ab: !s.ab } : { ...s }));
  return [{ key, ab }, ...aktuell.map((s) => ({ ...s }))].slice(0, Math.max(1, max));
}

export function sortRichtungSetzen(aktuell: readonly SortEintrag[], key: string, ab: boolean): SortEintrag[] {
  return aktuell.map((s) => (s.key === key ? { key: s.key, ab } : { ...s }));
}

export function sortEntfernen(aktuell: readonly SortEintrag[], key: string): SortEintrag[] {
  return aktuell.filter((s) => s.key !== key).map((s) => ({ ...s }));
}

export function sortNachVorn(aktuell: readonly SortEintrag[], key: string): SortEintrag[] {
  const e = aktuell.find((s) => s.key === key);
  if (!e) return aktuell.map((s) => ({ ...s }));
  return [{ ...e }, ...aktuell.filter((s) => s.key !== key).map((s) => ({ ...s }))];
}

/** "-num_calls,display_name" -> [{key: "num_calls", ab: true}, {key: "display_name", ab: false}] (so steht es auch in der Adresse). */
export function sortVonText(text: string | null | undefined, aliase: Record<string, string> = {}, max = MAX_SORTFELDER): SortEintrag[] {
  const ergebnis: SortEintrag[] = [];
  for (const teil of (text ?? "").split(",")) {
    const t = teil.trim();
    if (!t) continue;
    const ab = t.startsWith("-");
    const key = kanonisch(t.replace(/^-+/, ""), aliase);
    if (key && !ergebnis.some((e) => e.key === key)) ergebnis.push({ key, ab });
  }
  return ergebnis.slice(0, max);
}

export function sortZuText(sort: readonly SortEintrag[]): string {
  return sort.map((s) => `${s.ab ? "-" : ""}${s.key}`).join(",");
}

export function gleicheSortierung(a: readonly SortEintrag[], b: readonly SortEintrag[]): boolean {
  return a.length === b.length && a.every((x, i) => x.key === b[i].key && x.ab === b[i].ab);
}

/** Sortierung einer Smart View im Close-Format (`[{direction, field: {type, field_name | custom_field_id}}]`) als Schlüssel.
 *  Felder, die Close nur in der Suche kennt (z. B. Kontakte-Felder), bleiben ohne Eintrag. */
export function sortVonSmartView(sort: unknown): SortEintrag[] {
  if (!Array.isArray(sort)) return [];
  const ergebnis: SortEintrag[] = [];
  for (const roh of sort) {
    if (!roh || typeof roh !== "object") continue;
    const e = roh as { direction?: unknown; field?: { type?: unknown; field_name?: unknown; custom_field_id?: unknown; object_type?: unknown } };
    const f = e.field;
    if (!f || typeof f !== "object") continue;
    let key: string | null = null;
    if (f.type === "custom_field" && typeof f.custom_field_id === "string") key = `custom.${f.custom_field_id.replace(/^custom\./, "")}`;
    else if (f.type === "regular_field" && typeof f.field_name === "string" && (!f.object_type || f.object_type === "lead")) key = f.field_name;
    if (key && !ergebnis.some((x) => x.key === key)) ergebnis.push({ key, ab: e.direction === "desc" });
  }
  return ergebnis;
}

/** Umlaute und Groß-/Kleinschreibung vereinheitlichen (für Suche und Textvergleich). */
export function normalisiere(text: string): string {
  return text
    .toLocaleLowerCase("de")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

function istLeer(w: unknown): boolean {
  return w === null || w === undefined || w === "" || (Array.isArray(w) && w.length === 0);
}

function alsZahl(w: unknown): number | null {
  if (typeof w === "number") return Number.isFinite(w) ? w : null;
  if (typeof w === "string" && w.trim() !== "" && Number.isFinite(Number(w))) return Number(w);
  return null;
}

/** Vergleich zweier nicht leerer Werte nach Typ des Feldes (zahl, dauer: Zahl; zeit, datum: Zeitpunkt; sonst Text, "de", Zahlen im Text natürlich). */
export function vergleiche(a: unknown, b: unknown, typ: string): number {
  if (typ === "zahl" || typ === "dauer") {
    const x = alsZahl(a);
    const y = alsZahl(b);
    if (x !== null && y !== null) return x - y;
  }
  if (typ === "zeit" || typ === "datum") {
    const x = Date.parse(String(a));
    const y = Date.parse(String(b));
    if (Number.isFinite(x) && Number.isFinite(y)) return x - y;
  }
  if (typeof a === "number" && typeof b === "number") return a - b;
  const sa = Array.isArray(a) ? a.join(", ") : String(a);
  const sb = Array.isArray(b) ? b.join(", ") : String(b);
  return sa.localeCompare(sb, "de", { numeric: true, sensitivity: "base" });
}

/** Zeilen nach bis zu drei Feldern sortieren. Leere Werte stehen immer am Ende (auch absteigend), gleiche Werte behalten ihre Reihenfolge. */
export function sortiereZeilen<T>(zeilen: readonly T[], sort: readonly SortEintrag[], wert: (z: T, key: string) => unknown, typ: (key: string) => string): T[] {
  if (!sort.length) return [...zeilen];
  const indiziert = zeilen.map((z, i) => ({ z, i }));
  indiziert.sort((p, q) => {
    for (const s of sort) {
      const a = wert(p.z, s.key);
      const b = wert(q.z, s.key);
      const al = istLeer(a);
      const bl = istLeer(b);
      if (al && bl) continue;
      if (al) return 1;
      if (bl) return -1;
      const v = vergleiche(a, b, typ(s.key));
      if (v !== 0) return s.ab ? -v : v;
    }
    return p.i - q.i;
  });
  return indiziert.map((x) => x.z);
}

// ---------------------------------------------------------------------------
// Textsuche in der Liste
// ---------------------------------------------------------------------------

/** Alle Wörter der Suche kommen im Text vor (Umlaute und Schreibweise egal). Leere Suche trifft alles. */
export function textTrifft(text: string, suche: string): boolean {
  const woerter = normalisiere(suche).split(/\s+/).filter(Boolean);
  if (!woerter.length) return true;
  const heu = normalisiere(text);
  return woerter.every((w) => heu.includes(w));
}

// ---------------------------------------------------------------------------
// Gruppen im Katalog
// ---------------------------------------------------------------------------

export interface MitGruppe {
  gruppe?: string;
}

export function gruppiere<T extends MitGruppe>(felder: readonly T[], ohneGruppe = "Weitere"): { gruppe: string; felder: T[] }[] {
  const ergebnis: { gruppe: string; felder: T[] }[] = [];
  for (const f of felder) {
    const name = f.gruppe || ohneGruppe;
    let g = ergebnis.find((x) => x.gruppe === name);
    if (!g) ergebnis.push((g = { gruppe: name, felder: [] }));
    g.felder.push(f);
  }
  return ergebnis;
}

/** Sucht in Beschriftung und Gruppe (Umlaute egal). */
export function feldTrifft(feld: { label: string; gruppe?: string }, suche: string): boolean {
  return textTrifft(`${feld.label} ${feld.gruppe ?? ""}`, suche);
}

/** Fünf zuletzt genutzte Einträge, neueste zuerst (ISO-Zeiten); Einträge ohne Zeit fallen weg. */
export function zuletztGenutzt<T extends { id: string }>(eintraege: readonly T[], zeiten: Readonly<Record<string, string | null | undefined>>, ausser: string | null, max = 5): T[] {
  return eintraege
    .filter((e) => e.id !== ausser && zeiten[e.id])
    .sort((a, b) => String(zeiten[b.id]).localeCompare(String(zeiten[a.id])))
    .slice(0, max);
}

/** Name ohne führende Emojis und Zeichen (damit „📅 Simon · …“ und „Simon · …“ beieinander stehen). */
export function sortierName(name: string): string {
  return name.replace(/^[^\p{L}\p{N}]+/u, "").toLocaleLowerCase("de");
}
